import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiService } from '../../services/api'
import type { Order, PaginationParams, SearchFilters } from '../../types'

interface OrderState {
  orders: Order[]
  currentOrder: Order | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  filters: SearchFilters
}

const initialState: OrderState = {
  orders: [],
  currentOrder: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
}

export const fetchOrders = createAsyncThunk(
  'order/fetchOrders',
  async ({ filters, pagination }: { filters?: SearchFilters; pagination?: PaginationParams }) => {
    const response = await apiService.getOrders(filters, pagination)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data
  }
)

export const fetchOrderById = createAsyncThunk(
  'order/fetchOrderById',
  async (id: string | number) => {
    const response = await apiService.getOrder(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.order
  }
)

export const createOrder = createAsyncThunk(
  'order/createOrder',
  async (orderData: Partial<Order>) => {
    const response = await apiService.createOrder(orderData)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.order
  }
)

export const updateOrder = createAsyncThunk(
  'order/updateOrder',
  async ({ id, orderData }: { id: string | number; orderData: Partial<Order> }) => {
    const response = await apiService.updateOrder(id, orderData)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.order
  }
)

export const updateOrderStatus = createAsyncThunk(
  'order/updateOrderStatus',
  async ({ id, status, notes }: { id: string | number; status: string; notes?: string }) => {
    const response = await apiService.updateOrderStatus(id, status, notes)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.order
  }
)

export const deleteOrder = createAsyncThunk(
  'order/deleteOrder',
  async (id: string | number) => {
    const response = await apiService.deleteOrder(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return id
  }
)

export const bulkDeleteOrders = createAsyncThunk(
  'order/bulkDeleteOrders',
  async (ids: number[]) => {
    const response = await apiService.bulkDeleteOrders(ids)
    if (response.error) {
      throw new Error(response.error)
    }
    return ids
  }
)

export const bulkUpdateOrderStatus = createAsyncThunk(
  'order/bulkUpdateOrderStatus',
  async ({ ids, status }: { ids: number[]; status: string }) => {
    const response = await apiService.bulkUpdateOrderStatus(ids, status)
    if (response.error) {
      throw new Error(response.error)
    }
    return { ids, status }
  }
)

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setCurrentOrder: (state, action: PayloadAction<Order | null>) => {
      state.currentOrder = action.payload
    },
    setFilters: (state, action: PayloadAction<SearchFilters>) => {
      state.filters = action.payload
    },
    setPagination: (state, action: PayloadAction<Partial<OrderState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false
        state.orders = action.payload?.data || []
        state.pagination = {
          page: action.payload?.meta?.page || 1,
          limit: action.payload?.meta?.limit || 10,
          total: action.payload?.meta?.total || 0,
          totalPages: action.payload?.meta?.totalPages || 0,
        }
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch orders'
      })
      // Fetch order by id
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false
        state.currentOrder = action.payload || null
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch order'
      })
      // Create order
      .addCase(createOrder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.orders.unshift(action.payload)
        }
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create order'
      })
      // Update order
      .addCase(updateOrder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          const payload = action.payload
          const index = state.orders.findIndex((o) => o.id === payload.id)
          if (index !== -1) {
            state.orders[index] = payload
          }
          if (state.currentOrder?.id === payload.id) {
            state.currentOrder = payload
          }
        }
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update order'
      })
      // Update order status
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        if (action.payload) {
          const payload = action.payload
          const index = state.orders.findIndex((o) => o.id === payload.id)
          if (index !== -1) {
            state.orders[index] = payload
          }
          if (state.currentOrder?.id === payload.id) {
            state.currentOrder = payload
          }
        }
      })
      // Delete order
      .addCase(deleteOrder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.loading = false
        state.orders = state.orders.filter((o) => o.id !== action.payload)
        if (state.currentOrder?.id === action.payload) {
          state.currentOrder = null
        }
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete order'
      })
      // Bulk delete orders
      .addCase(bulkDeleteOrders.fulfilled, (state, action) => {
        state.orders = state.orders.filter((o) => !action.payload.includes(o.id as number))
        if (state.currentOrder && action.payload.includes(state.currentOrder.id as number)) {
          state.currentOrder = null
        }
      })
      // Bulk update order status
      .addCase(bulkUpdateOrderStatus.fulfilled, (state, action) => {
        const { ids, status } = action.payload
        const validStatus = status as Order['status']
        state.orders = state.orders.map((o) => {
          if (ids.includes(o.id as number)) {
            return { ...o, status: validStatus }
          }
          return o
        })
        if (state.currentOrder && ids.includes(state.currentOrder.id as number)) {
          state.currentOrder = { ...state.currentOrder, status: validStatus }
        }
      })
  },
})

export const { setCurrentOrder, setFilters, setPagination, clearError } = orderSlice.actions
export default orderSlice.reducer
